import { cva } from 'class-variance-authority';

export const comparisonIconColorClasses = {
  default: 'text-gray-500',
  added: 'text-green-500',
  removed: 'text-red-500',
  modified: 'text-amber-500',
} as const;

export type ComparisonStatus = keyof typeof comparisonIconColorClasses;

export const sidebarHeaderTitleClasses = cva(['text-base', 'leading-6', 'font-normal', 'text-black']);
export const sidebarSectionWrapperClasses = cva(['w-full', 'px-2', 'text-sm', 'select-none']);
export const sidebarBlockListClasses = cva(['flex flex-col gap-1']);
export const sidebarBlockIconClasses = cva(['p-1', 'h-6', 'w-6', 'rounded-sm', 'shrink-0']);

export const sidebarContainerClasses = cva(['flex', 'h-full', 'flex-col', 'border-r', 'bg-white'], {
  variants: {
    maxWidth: {
      drawer: 'max-w-drawer w-1/3',
      fixed: '',
    },
  },
  defaultVariants: {
    maxWidth: 'fixed',
  },
});

export const sidebarHeaderClasses = cva(
  ['flex', 'h-[50px]', 'shrink-0', 'items-center', 'justify-between', 'gap-4', 'px-4', 'py-3'],
  {
    variants: {
      withBorder: {
        true: 'border-b',
        false: '',
      },
    },
    defaultVariants: {
      withBorder: false,
    },
  },
);

export const sidebarFooterClasses = cva([
  'text-info-600',
  'flex',
  'h-[53px]',
  'shrink-0',
  'items-center',
  'gap-x-1.5',
  'border-t',
  'px-4',
  'text-sm',
  'leading-[18px]',
  'font-normal',
]);

export const sidebarListControlClasses = cva('cursor-pointer transition-colors', {
  variants: {
    isHighlighted: {
      false: null,
      true: 'text-primary-600',
    },
  },
});

export const sidebarSectionRowClasses = cva(
  ['group', 'flex', 'w-full', 'place-items-center', 'gap-x-2', 'rounded-sm', 'p-1', 'mb-1'],
  {
    variants: {
      interactive: {
        true: 'hover:bg-info-50 cursor-pointer',
        false: '',
      },
      removed: {
        true: 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700',
        false: '',
      },
      added: {
        true: 'bg-green-50 text-green-500 hover:bg-green-100 hover:text-green-700',
        false: '',
      },
    },
    defaultVariants: {
      interactive: true,
      removed: false,
      added: false,
    },
  },
);

export const sidebarSectionChevronClasses = cva(['text-info-800', 'h-5', 'w-3', 'shrink-0'], {
  variants: {
    interactive: {
      true: 'cursor-pointer',
      false: 'pointer-events-none opacity-25',
    },
  },
  defaultVariants: {
    interactive: true,
  },
});

export const sidebarSectionIconClasses = cva(
  ['align-center', 'inline-flex', 'h-6', 'w-6', 'items-center', 'rounded-sm', 'p-1'],
  {
    variants: {
      active: {
        true: 'text-primary-600',
      },
      disabled: {
        true: 'opacity-25',
        false: '',
      },
      removed: {
        true: 'text-red-500',
      },
      added: {
        true: 'text-green-500',
      },
    },
    compoundVariants: [{ removed: false, added: false, class: 'text-info-500' }],
    defaultVariants: {
      active: false,
      disabled: false,
      removed: false,
      added: false,
    },
  },
);

export const sidebarSectionTitleClasses = cva(['grow', 'truncate', 'text-left', 'font-bold'], {
  variants: {
    disabled: {
      true: 'opacity-25',
      false: '',
    },
    removed: {
      true: 'text-red-800',
    },
    added: {
      true: 'text-green-800',
    },
  },
  compoundVariants: [
    {
      removed: false,
      added: false,
      class: 'text-info-800',
    },
  ],
  defaultVariants: {
    disabled: false,
    removed: false,
    added: false,
  },
});

export const sidebarBlockItemClasses = cva(['group', 'w-full', 'cursor-pointer', 'rounded-sm', 'p-1', 'text-sm'], {
  variants: {
    status: {
      default: 'hover:bg-info-50',
      added: 'bg-green-50 hover:bg-green-100',
      removed: 'bg-red-50 hover:bg-red-100',
      modified: 'bg-amber-50 hover:bg-amber-100',
    },
  },
  defaultVariants: {
    status: 'default',
  },
});

export const sidebarBlockTextClasses = cva(
  ['min-w-0', 'truncate', 'font-normal', 'cursor-pointer', 'group-hover:text-info-900'],
  {
    variants: {
      bold: {
        true: 'font-bold',
        false: '',
      },
      dimmed: {
        true: 'opacity-25',
        false: '',
      },
      status: {
        default: 'text-info-700',
        added: 'text-green-800',
        removed: 'text-red-800',
        modified: 'text-amber-800',
      },
    },
    defaultVariants: {
      bold: false,
      status: 'default',
    },
  },
);
